import React, { useState, useEffect, useMemo } from 'react';
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { TanStackTable } from '../Tables/TanStackTable';
import { BaseCard } from '../Cards/BaseCard';
import { useBoundStore } from '../../Store/store';

export const MailHistoryList = ({ filters = {} }) => {
    const { mailHistory,mailMetaData, getMailHistory } = useBoundStore();
    const {t} = useTranslation();
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [forceNewData, setForceNewData] = useState(false);

    const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]);

    useEffect(() => {
        fetchMailHistory().then(r => {});
    }, [memoizedFilters, currentPage]);

    const fetchMailHistory = async () => {
        setLoading(true);
        try {
            await getMailHistory({
                ...memoizedFilters,
                page: currentPage
            });
        } catch (error) {
            console.error('Error fetching mail history:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleRowExpansion = (rowId) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(rowId)) {
            newExpandedRows.delete(rowId);
        } else {
            newExpandedRows.add(rowId);
        }
        setExpandedRows(newExpandedRows);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('nl-NL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getMailTypeBadge = (mailType) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        const types = {
            'customer_email': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Customer' },
            'freelancer_email': { bg: 'bg-purple-100', text: 'text-purple-800', label: i18next.t('shifts_module.column_freelancer') },
            'shift_email': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Shift' },
            'system_email': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'System' }
        };

        const type = types[mailType] || types['system_email'];
        return `${baseClasses} ${type.bg} ${type.text}`;
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'expand',
            header: '',
            cell: ({ row }) => (
                <button
                    onClick={() => toggleRowExpansion(row.original.id)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                    {expandedRows.has(row.original.id) ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    )}
                </button>
            ),
            enableSorting: false,
        },
        {
            accessorKey: 'subject',
            header: t('tables.subject'),
            cell: ({ getValue }) => (
                <div className="max-w-xs truncate" title={getValue()}>
                    {getValue()}
                </div>
            ),
        },
        {
            accessorKey: 'recipient_email',
            header: t('tables.recipient'),
            cell: ({ getValue }) => (
                <div className="text-sm text-gray-600">{getValue()}</div>
            ),
        },
        {
            accessorKey: 'sent_at',
            header: t('tables.sent_on'),
            cell: ({ getValue }) => (
                <div className="text-sm text-gray-600">
                    {formatDate(getValue())}
                </div>
            ),
        },
        {
            accessorKey: 'customer',
            header: t('tables.customer'),
            cell: ({ getValue }) => {
                const customer = getValue();
                return customer ? (
                    <div className="text-sm">
                        <div className="font-medium">{customer.name}</div>
                        <div className="text-gray-500">{customer.email}</div>
                    </div>
                ) : (
                    <span className="text-gray-400">-</span>
                );
            },
        },
        {
            accessorKey: 'freelancer',
            header: i18next.t('shifts_module.column_freelancer'),
            cell: ({ getValue }) => {
                const freelancer = getValue();
                return freelancer ? (
                    <div className="text-sm">
                        <div className="font-medium">{freelancer.name}</div>
                        <div className="text-gray-500">{freelancer.email}</div>
                    </div>
                ) : (
                    <span className="text-gray-400">-</span>
                );
            },
        },
    ], [expandedRows, t]);

    const ExpandedContent = ({ mail }) => {
        const [showRawContent, setShowRawContent] = useState(false);

        return (
            <div className="bg-gray-50 p-4 rounded-lg mt-2">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h4 className="font-semibold text-gray-900">Email</h4>
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setShowRawContent(!showRawContent)}
                            className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                        >
                            {showRawContent ? 'Show Rendered' : 'Show Raw HTML'}
                        </button>
                    </div>
                </div>

                {mail.content && (
                    <div className="border rounded-lg overflow-hidden">
                        {showRawContent ? (
                            <pre className="bg-gray-100 p-4 text-xs overflow-auto max-h-96 text-gray-800">
                                {mail.content.content}
                            </pre>
                        ) : (
                            <div className="bg-white">
                                {mail.content.content_type === 'html' ? (
                                    <iframe
                                        srcDoc={mail.content.content}
                                        className="w-full h-96 border-0"
                                        title={`Email content for ${mail.subject}`}
                                        sandbox="allow-same-origin"
                                    />
                                ) : (
                                    <div className="p-4 whitespace-pre-wrap text-sm">
                                        {mail.content.content}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {mail.shift && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <h5 className="font-medium text-blue-900 mb-2">Gerelateerde shift</h5>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <BaseCard>
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <span className="ml-2 text-gray-600">Mailgeschiedenis aan het ophalen...</span>
                </div>
            </BaseCard>
        );
    }

    return (
        <>
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('general.labels.mail_history')}</h3>
                <p className="text-sm text-gray-600">
                    {t('general.labels.total_mails', {count: mailHistory?.length || 0})}
                </p>
            </div>

            {mailHistory && mailHistory.length > 0 ? (
                <div className="space-y-2">
                    <div className="overflow-x-auto">
                        <TanStackTable
                            columns={columns}
                            tableData={mailHistory}
                            laravelPaginationMetaData={mailMetaData}
                            forceNewData={forceNewData}
                            onChangePage={(page) => {
                                setCurrentPage(page + 1)
                            }}
                        />
                    </div>

                    {mailHistory.map((mail) =>
                        expandedRows.has(mail.id) && (
                            <div key={`expanded-${mail.id}`} className="ml-8">
                                <ExpandedContent mail={mail} />
                            </div>
                        )
                    )}
                </div>
            ) : (
                <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t('general.labels.no_emails')}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('general.labels.no_mail_history')}
                    </p>
                </div>
            )}
        </>
    );
};
